import { Language } from "./i18n";
import { loadSettings, saveSettings } from "./settings";

const LANDING_ID = "landing";
const PLAY_ID = "landing-play";
const LANG_SELECT_ID = "landing-lang";

/** Delay after the smooth scroll-to-top before the overlay is dismissed on Play. */
const SCROLL_TO_TOP_MS = 380;

/**
 * Removes the static landing overlay from the DOM (idempotent). Used on the
 * suppressed boot path (verification queries / deep-links / community &
 * local-load reloads) so crawlers still see the landing in the initial HTML
 * while returning visitors and Playwright runs drop straight into the app.
 */
export function removeLanding(): void {
  document.getElementById(LANDING_ID)?.remove();
}

/**
 * True while the landing overlay is still in the DOM. The frame loop uses this
 * (unioned with isTitleActive) to freeze the simulation, so a previously saved
 * scene cannot play audibly behind the landing once a stray tap on the overlay
 * resumes the AudioContext. The landing outlives the Play press until the title
 * has painted its first frame, and the title owns the frame from its start, so
 * the two gates overlap without a gap.
 */
export function isLandingActive(): boolean {
  return document.getElementById(LANDING_ID) !== null;
}

/**
 * Reflects the visitor's language on the landing and binds the dropdown to
 * switch it. data-lang lives on the root element (a tiny <head> script stamps
 * it before first paint to avoid a flash of both languages); this re-affirms it
 * from the app's saved language and wires the dropdown. Switching persists the
 * choice to settings and reloads: the app behind the overlay has already built
 * its UI in the boot language, so a reload (the same mechanism the in-app
 * language setting uses) is the only way the choice reaches it. The head
 * script re-stamps data-lang from the saved value before first paint, so the
 * reloaded landing comes back in the new language without a bilingual flash.
 */
export function applyLandingLanguage(lang: Language): void {
  const el = document.getElementById(LANDING_ID);
  if (el === null) {
    return;
  }
  document.documentElement.dataset.lang = lang;
  const select = document.getElementById(LANG_SELECT_ID);
  if (!(select instanceof HTMLSelectElement)) {
    return;
  }
  select.value = lang;
  select.addEventListener("change", (): void => {
    const next = select.value;
    if (next !== "ja" && next !== "en") {
      return;
    }
    document.documentElement.dataset.lang = next;
    const settings = loadSettings();
    if (settings.language === next) {
      return;
    }
    settings.language = next;
    saveSettings(settings);
    location.reload();
  });
}

/**
 * Shows the landing overlay (already present as static HTML) and resolves once
 * the visitor presses Play, after the scroll container returns to the top so
 * the hero logo is on screen. The overlay is deliberately NOT removed here: the
 * caller starts the title animation behind it and removes the landing only once
 * the title has painted its first frame, so the "Resonella" word never blinks
 * out between the two. The Play press is a user gesture, so the editor's global
 * pointerdown listener unlocks the AudioContext on the same tap. If the overlay
 * is absent the gate resolves immediately.
 */
export function showLanding(): Promise<void> {
  const el = document.getElementById(LANDING_ID);
  if (el === null) {
    return Promise.resolve();
  }
  return new Promise<void>((resolve: () => void): void => {
    const play = document.getElementById(PLAY_ID);
    if (play === null) {
      resolve();
      return;
    }
    play.addEventListener("click", (): void => {
      el.classList.add("leaving");
      const scroll = el.querySelector<HTMLElement>(".landing-scroll");
      scroll?.scrollTo({ top: 0, behavior: "smooth" });
      window.setTimeout(resolve, SCROLL_TO_TOP_MS);
    }, { once: true });
  });
}
